import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  UserPlus, 
  Search, 
  Calendar as CalendarIcon, 
  Clock, 
  FileText,
  Video,
  User,
  X,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Check,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Plus,
  Upload,
  Phone
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { normalizePhone, isValidMobilePhone } from '../utils/phoneUtils';
import './AppointmentModal.css';

const defaultInsurances = [
  { name: 'PARTICULAR', plans: ['-'] },
  { name: 'A B G', plans: ['Plan 210', 'Plan 310'] },
  { name: 'OSDE', plans: ['Binario', '210', '310', '410', '450', '510'] },
  { name: 'SWISS MEDICAL', plans: ['Nubial', 'SMG02', 'SMG20', 'SMG40', 'SMG50'] },
  { name: 'GALENO', plans: ['Blanco', 'Azul', 'Plata', 'Oro'] },
  { name: 'MEDICUS', plans: ['Azul', 'Celeste', 'Family'] },
  { name: 'OMINT', plans: ['O', 'F', 'S', 'Premium'] },
  { name: 'MEDIFE', plans: ['Bronce', 'Plata', 'Oro', 'Platinum'] },
  { name: 'SANCOR SALUD', plans: ['1000', '2000', '3000', '4000'] },
  { name: 'ACCORD SALUD', plans: ['Accord 110', 'Accord 210', 'Accord 310'] },
  { name: 'PAMI', plans: ['General', 'Especialista'] },
  { name: 'IOMA', plans: ['General', 'Accesorios'] },
  { name: 'UNIóN PERSONAL', plans: ['Classic', 'Familiar', 'Accord'] },
  { name: 'PREVENCION SALUD', plans: ['A1', 'A2', 'A3', 'A4'] },
  { name: 'HOMIC', plans: ['H1', 'H2', 'H3'] },
  { name: 'OSECAC', plans: ['General'] }
];

const getInsurances = () => {
  try {
    const saved = localStorage.getItem('gdt_insurances');
    const parsed = saved ? JSON.parse(saved) : null;
    const list = (parsed && Array.isArray(parsed)) ? parsed : [...defaultInsurances];
    if (!list.find(i => i.name === 'PARTICULAR')) list.unshift({ name: 'PARTICULAR', plans: ['-'] });
    return list.filter(i => i.active !== false);
  } catch(e) { return defaultInsurances; }
};

const AppointmentModal = ({ isOpen, onClose, initialData, onSave, professionalsList = [] }) => {
  const insurancesList = React.useMemo(() => getInsurances(), [isOpen]);
  const { addNotification } = useNotifications();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Load active staff
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff?.full_name || '';
  const rawProf = initialData?.professional || initialData?.professional_name;
  const initialProfessional = (rawProf && !rawProf.startsWith('VER '))
    ? rawProf 
    : (activeStaff.type === 'Profesional' ? staffName : null) || professionalsList?.filter(p => p.type === 'Profesional' && !p.is_hidden)?.[0]?.full_name || '';
  const initialSpecialty = initialData?.specialty || professionalsList?.find(p => p.full_name === initialProfessional)?.specialty || 'CONSULTA GENERAL';

  const [formData, setFormData] = useState({
    id: initialData?.id || null,
    patient_id: initialData?.patient_id || null,
    patient_dni: initialData?.patient_dni || '',
    studyType: initialData?.studyType || initialData?.study_type || 'CONSULTA GENERAL',
    patient: initialData?.patient || initialData?.patient_name || '',
    date: initialData?.date || initialData?.appointment_date || '',
    time: initialData?.time || initialData?.appointment_time || '',
    insurance: initialData?.insurance || 'PARTICULAR',
    plan: initialData?.plan || '-',
    professional: initialProfessional,
    specialty: initialSpecialty,
    type: 'presencial',
    notes: initialData?.notes || initialData?.admin_notes || ''
  });

  const [attachedFiles, setAttachedFiles] = useState(initialData?.study_details?.attached_files || []);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
      
      setAttachedFiles(prev => [...prev, { name: file.name, url: publicUrl, study_name: 'Adicional' }]);
      addNotification({ title: 'Éxito', message: 'Archivo adjuntado correctamente', type: 'success' });
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo subir el archivo', type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const [activeTab, setActiveTab] = useState('datos');
  const [patientSearch, setPatientSearch] = useState(initialData?.patient || '');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const currentPlans = React.useMemo(() => {
    const ins = insurancesList.find(i => i.name === formData.insurance);
    return ins?.plans || ['-'];
  }, [formData.insurance, insurancesList]);

  // New patient state
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [showNewPatientResults, setShowNewPatientResults] = useState(false);
  const [newPatientSearchResults, setNewPatientSearchResults] = useState([]);
  const [newPatient, setNewPatient] = useState({
    full_name: '',
    dni: '',
    phone: '+549',
    email: '',
    insurance_provider: 'PARTICULAR',
    insurance_plan: '-',
    affiliate_number: ''
  });

  const newPatientPlans = React.useMemo(() => {
    const ins = insurancesList.find(i => i.name === newPatient.insurance_provider);
    return ins?.plans || ['-'];
  }, [newPatient.insurance_provider, insurancesList]);

  const [isSavingPatient, setIsSavingPatient] = useState(false);

  const [patientAppointments, setPatientAppointments] = useState([]);
  const [turnosSubTab, setTurnosSubTab] = useState('pasados');
  const [expandedHistoryAppId, setExpandedHistoryAppId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const pName = initialData?.patient || initialData?.patient_name || '';
      const pDni = initialData?.patient_dni || initialData?.study_details?.patient_dni || '';
      const pPhone = initialData?.patient_phone || initialData?.phone || initialData?.study_details?.phone || '';
      const pEmail = initialData?.patient_email || initialData?.email || initialData?.study_details?.email || '';
      const pInsurance = initialData?.insurance || initialData?.insurance_provider || initialData?.study_details?.insurance_provider || 'PARTICULAR';
      const pPlan = initialData?.plan || initialData?.insurance_plan || initialData?.study_details?.insurance_plan || '-';
      const pId = initialData?.patient_id || null;

      setFormData({
        id: initialData?.id || null,
        patient_id: pId,
        patient_dni: pDni,
        patient_email: pEmail,
        patient_phone: pPhone,
        studyType: initialData?.studyType || initialData?.study_type || 'CONSULTA GENERAL',
        patient: pName,
        date: initialData?.date || initialData?.appointment_date || '',
        time: initialData?.time || initialData?.appointment_time || '',
        insurance: pInsurance,
        plan: pPlan,
        professional: (initialData?.professional && !initialData.professional.startsWith('VER '))
          ? initialData.professional 
          : (initialData?.professional_name && !initialData.professional_name.startsWith('VER '))
            ? initialData.professional_name
            : (activeStaff.type === 'Profesional' ? staffName : null) || professionalsList?.filter(p => p.type === 'Profesional' && !p.is_hidden)?.[0]?.full_name || '',
        specialty: initialData?.specialty || initialSpecialty,
        type: 'presencial',
        notes: initialData?.notes || initialData?.admin_notes || ''
      });
      setAttachedFiles(initialData?.study_details?.attached_files || initialData?.attached_files || []);
      setPatientSearch(pName);
      setActiveTab('datos');
      setShowCreatePatient(false);

      if (pId || pDni || pName) {
        const fetchPatientRecord = async () => {
          try {
            let q = supabase.from('patients').select('*');
            if (pId) {
              q = q.eq('id', pId);
            } else if (pDni) {
              q = q.eq('dni', pDni);
            } else if (pName) {
              q = q.ilike('full_name', pName.trim());
            }

            const { data: patData } = await q.maybeSingle();
            if (patData) {
              setFormData(prev => ({
                ...prev,
                patient_id: patData.id || prev.patient_id,
                patient: patData.full_name || prev.patient,
                patient_dni: patData.dni || prev.patient_dni,
                patient_email: patData.email || prev.patient_email,
                patient_phone: patData.phone || prev.patient_phone,
                insurance: patData.insurance_provider || prev.insurance,
                plan: patData.insurance_plan || prev.plan
              }));
              if (patData.full_name) setPatientSearch(patData.full_name);
            }
          } catch (err) {
            console.error('Error auto-fetching patient details in AppointmentModal:', err);
          }
        };
        fetchPatientRecord();

        const fetchHistory = async () => {
          try {
            let hq = supabase.from('appointments').select('*').order('appointment_date', { ascending: false });
            if (pDni) {
              hq = hq.eq('patient_dni', String(pDni).trim());
            } else if (pName) {
              hq = hq.ilike('patient_name', pName.trim());
            } else {
              return;
            }
            const { data: hData } = await hq;
            setPatientAppointments(hData || []);
          } catch (err) {
            console.error('Error fetching patient history in AppointmentModal:', err);
          }
        };
        fetchHistory();
      } else {
        setPatientAppointments([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  const [conflictAppointment, setConflictAppointment] = useState(null);

  useEffect(() => {
    if (!isOpen || !formData.date || !formData.time || (!formData.patient && !formData.patient_dni)) {
      setConflictAppointment(null);
      return;
    }

    const checkPatientConflict = async () => {
      try {
        const normName = (formData.patient || '').replace(/[\s\u00a0]+/g, ' ').trim();
        const normDni = (formData.patient_dni || '').replace(/[^0-9]/g, '').trim();

        const formattedTime = formData.time.length === 5 ? `${formData.time}:00` : formData.time;

        let query = supabase.from('appointments')
          .select('id, professional_name, appointment_time, appointment_date, study_type, patient_name, patient_dni, status')
          .eq('appointment_date', formData.date)
          .eq('appointment_time', formattedTime)
          .neq('status', 'cancelado');

        if (formData.professional && !formData.professional.startsWith('VER ')) {
          query = query.eq('professional_name', formData.professional);
        }

        if (formData.id) {
          query = query.neq('id', formData.id);
        }

        const { data, error } = await query;
        if (error || !data || data.length === 0) {
          setConflictAppointment(null);
          return;
        }

        const match = data.find(app => {
          // Conflict occurs ONLY if the SAME time slot is already taken by a DIFFERENT patient or same patient
          // But wait: if it's the SAME patient at the SAME date and time with SAME prof, it's a conflict.
          const appDni = (app.patient_dni || '').replace(/[^0-9]/g, '').trim();
          const appName = (app.patient_name || '').replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();
          
          const isSameDni = normDni && appDni && normDni === appDni;
          const isSameName = normName && appName && normName.toLowerCase() === appName;

          if (isSameDni || isSameName) return true;
          return false;
        });

        setConflictAppointment(match || null);
      } catch (err) {
        console.error("Error checking patient appointment conflict:", err);
      }
    };

    const timer = setTimeout(checkPatientConflict, 300);
    return () => clearTimeout(timer);
  }, [isOpen, formData.date, formData.time, formData.patient, formData.patient_dni, formData.id]);

  if (!isOpen) return null;

  const searchNewPatients = async (query) => {
    if (query.length < 2) {
      setNewPatientSearchResults([]);
      setShowNewPatientResults(false);
      return;
    }
    setShowNewPatientResults(true);
    try {
      let queryBuilder = supabase.from('patients').select('*');
      const cleanedRaw = query.replace(/[,()"\\]/g, ' ').trim();
      const terms = cleanedRaw.split(/\s+/).filter(Boolean);
      const safeQuery = query.replace(/[,()"\\]/g, '').trim();
      const cleanQuery = query.replace(/[^0-9a-zA-Z]/g, '');
      const wildcardDni = cleanQuery.length > 0 ? cleanQuery.split('').join('%') : cleanQuery;
      
      if (terms.length > 0) {
        const andFullName = `and(${terms.map(t => `full_name.ilike.%${t.replace(/[%_]/g, '')}%`).join(',')})`;
        let orList = [`${andFullName}`];
        if (safeQuery) orList.push(`dni.ilike.%${safeQuery}%`);
        if (wildcardDni) orList.push(`dni.ilike.%${wildcardDni}%`);

        const { data, error } = await queryBuilder
          .or(orList.join(','))
          .limit(5);
        if (error) throw error;
        setNewPatientSearchResults(data || []);
      } else {
        setNewPatientSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const searchPatients = async (query) => {
    setPatientSearch(query);
    setFormData(prev => ({ ...prev, patient: query }));
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);
    try {
      let queryBuilder = supabase.from('patients').select('*');
      
      const cleanedRaw = query.replace(/[,()"\\]/g, ' ').trim();
      const terms = cleanedRaw.split(/\s+/).filter(Boolean);
      const safeQuery = query.replace(/[,()"\\]/g, '').trim();
      const cleanQuery = query.replace(/[^0-9a-zA-Z]/g, '');
      const wildcardDni = cleanQuery.length > 0 ? cleanQuery.split('').join('%') : cleanQuery;

      if (terms.length > 0) {
        const andFullName = `and(${terms.map(t => `full_name.ilike.%${t.replace(/[%_]/g, '')}%`).join(',')})`;
        let orList = [`${andFullName}`];
        if (safeQuery) orList.push(`dni.ilike.%${safeQuery}%`);
        if (wildcardDni) orList.push(`dni.ilike.%${wildcardDni}%`);

        const { data, error } = await queryBuilder
          .or(orList.join(','))
          .limit(5);

        if (error) throw error;
        setSearchResults(data || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const selectPatient = (p) => {
    const fullName = p.full_name;
    setPatientSearch(fullName);
    setFormData(prev => ({
      ...prev,
      patient: fullName,
      patient_id: p.id,
      patient_dni: p.dni || prev.patient_dni || '',
      patient_email: p.email || prev.patient_email || '',
      patient_phone: p.phone || prev.patient_phone || '',
      insurance: p.insurance_provider || prev.insurance || 'PARTICULAR',
      plan: p.insurance_plan || prev.plan || '-'
    }));
    setShowResults(false);
  };

  const handleSaveNewPatient = async (e) => {
    e.preventDefault();
    if (!newPatient.full_name.trim() || !newPatient.dni.trim() || !newPatient.phone.trim()) {
      addNotification('Nombre completo, DNI y Teléfono son requeridos', 'error');
      return;
    }

    if (!isValidMobilePhone(newPatient.phone)) {
      addNotification(`El teléfono (${newPatient.phone}) no es un celular válido. Formato esperado: +54 9 11 ...`, 'warning');
    }

    const cleanPhone = normalizePhone(newPatient.phone);

    setIsSavingPatient(true);
    try {
      // Check existing patient by DNI or Name
      let existingMatch = null;
      if (newPatient.dni.trim()) {
        const { data: existingDni } = await supabase
          .from('patients')
          .select('*')
          .eq('dni', newPatient.dni.trim())
          .maybeSingle();
        if (existingDni) existingMatch = existingDni;
      }

      if (!existingMatch && newPatient.full_name.trim()) {
        const cleanName = newPatient.full_name.trim().toUpperCase();
        const { data: existingName } = await supabase
          .from('patients')
          .select('*')
          .ilike('full_name', cleanName)
          .maybeSingle();
        if (existingName) existingMatch = existingName;
      }
          
      if (existingMatch) {
        selectPatient(existingMatch);
        setShowCreatePatient(false);
        addNotification(`⚠️ El paciente ya existía en la base de datos (${existingMatch.full_name}). Se ha seleccionado automáticamente su ficha existente.`, 'warning');
        setIsSavingPatient(false);
        return;
      }

      const { data, error } = await supabase
        .from('patients')
        .insert([{
          full_name: newPatient.full_name.trim().toUpperCase(),
          dni: newPatient.dni.trim() || null,
          phone: cleanPhone || null,
          email: newPatient.email.trim() || null,
          insurance_provider: newPatient.insurance_provider,
          insurance_plan: newPatient.insurance_plan === '-' ? null : newPatient.insurance_plan,
          affiliate_number: newPatient.insurance_provider !== 'PARTICULAR' ? newPatient.affiliate_number.trim() || null : null
        }])
        .select();

      if (error) throw error;

      if (data && data.length > 0) {
        const createdPatient = data[0];
        selectPatient(createdPatient);
        addNotification('Paciente registrado y seleccionado con éxito', 'success');
      } else {
        // Fallback
        setPatientSearch(newPatient.full_name.trim().toUpperCase());
        setFormData({
          ...formData,
          patient: newPatient.full_name.trim().toUpperCase(),
          patient_email: newPatient.email.trim() || '',
          patient_phone: newPatient.phone.trim() || '',
          insurance: newPatient.insurance_provider,
          plan: newPatient.insurance_plan
        });
        addNotification('Paciente registrado con éxito', 'success');
      }

      // Reset form and close
      setNewPatient({
        full_name: '',
        dni: '',
        phone: '+549',
        email: '',
        insurance_provider: 'PARTICULAR',
        insurance_plan: '-',
        affiliate_number: ''
      });
      setShowCreatePatient(false);
    } catch (err) {
      console.error('Error saving patient:', err);
      addNotification('Error al registrar: ' + err.message, 'error');
    } finally {
      setIsSavingPatient(false);
    }
  };

  return createPortal(
    <div className="appointment-modal-overlay" style={{ zIndex: 100005 }} onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}>
      <div className="appointment-modal-container" onClick={e => e.stopPropagation()}>
        <div className="appointment-modal-header">
          <div className="header-title">
            <CalendarIcon size={20} />
            <span>{formData.id ? 'MODIFICAR TURNO' : 'DATOS DEL TURNO'}</span>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="appointment-modal-tabs">
          <button 
            className={`modal-tab ${activeTab === 'datos' ? 'active' : ''}`}
            onClick={() => setActiveTab('datos')}
          >
            DATOS PRINCIPALES
          </button>
          <button 
            className={`modal-tab ${activeTab === 'historial' ? 'active' : ''}`}
            onClick={() => setActiveTab('historial')}
          >
            HISTORIAL DE TURNOS
          </button>
        </div>

        <div className="appointment-modal-body">
          {activeTab === 'datos' && (
          <div className="form-section" style={{ display: 'block', width: '100%' }}>
            {conflictAppointment && (
              <div style={{
                background: 'rgba(255, 171, 0, 0.14)',
                border: '1px solid rgba(255, 171, 0, 0.4)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: '#ffb300',
                fontSize: '0.85rem'
              }}>
                <AlertTriangle size={24} style={{ flexShrink: 0, color: '#ffb300' }} />
                <div>
                  <strong style={{ color: '#ffd54f', display: 'block', marginBottom: '2px', fontSize: '0.9rem' }}>
                    ⚠️ ¡ALERTA DE SOBREPOSICIÓN DE TURNO!
                  </strong>
                  El paciente ya tiene un turno asignado la misma fecha (<strong>{conflictAppointment.appointment_date}</strong>) a las <strong>{conflictAppointment.appointment_time?.substring(0, 5)} hs</strong> con <strong>{conflictAppointment.professional_name}</strong> ({conflictAppointment.study_type || 'Consulta'}).
                </div>
              </div>
            )}
            <div className="form-row" style={{ width: '100%' }}>
              <div className="form-group full" style={{ width: '100%', flex: '1 1 100%' }}>
                <label>PACIENTE</label>
                <div className="patient-search-wrapper" style={{ width: '100%', flex: '1 1 100%' }}>
                    <div className="search-input-box">
                      <div className="search-icon-box">
                        {isSearching ? <Loader2 size={16} className="search-icon spinning" /> : <Search size={16} className="search-icon" />}
                      </div>
                      <input 
                        type="text" 
                        placeholder="ESCRIBA APELLIDO, NOMBRE O DNI..." 
                        value={patientSearch}
                        onChange={(e) => searchPatients(e.target.value)}
                        onFocus={() => patientSearch.length >= 2 && setShowResults(true)}
                      />
                                  {showResults && (
                        <div className="search-results-dropdown-modal" style={{ zIndex: 100 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: 'rgba(0,0,0,0.6)', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            <span>PACIENTES REGISTRADOS ({searchResults.length})</span>
                            <button 
                              type="button" 
                              onClick={() => setShowResults(false)}
                              style={{ background: 'none', border: 'none', color: '#ff5252', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                            >
                              ✕ CERRAR SUGERENCIAS
                            </button>
                          </div>

                          {searchResults.length > 0 ? (
                            searchResults.map(p => (
                              <div 
                                key={p.id} 
                                className="search-result-item"
                                onClick={() => selectPatient(p)}
                              >
                                <div className="result-avatar"><User size={14} /></div>
                                <div className="result-info">
                                  <span className="result-name">{p.full_name}</span>
                                  <span className="result-dni" style={{ fontSize: '0.75rem', color: '#00e5ff' }}>
                                    DNI: {p.dni || 'Sin DNI'} {p.birthdate ? `| Nac: ${p.birthdate}` : ''}
                                  </span>
                                </div>
                                <div className="result-insurance" style={{ fontSize: '0.75rem', color: '#ffd54f', fontWeight: 600 }}>
                                  {p.insurance_provider || 'PARTICULAR'} {p.insurance_plan ? `(${p.insurance_plan})` : ''}
                                </div>
                              </div>
                            ))
                          ) : !isSearching && (
                            <div className="no-results-msg">
                              <AlertCircle size={14} />
                              <span>No se encontró el paciente. Puede cargarlo como nuevo.</span>
                            </div>
                          )}

                          <div 
                            className="search-result-item create-new-option"
                            style={{ background: 'rgba(0, 229, 255, 0.12)', borderTop: '1px solid rgba(0, 229, 255, 0.3)', color: '#00e5ff', cursor: 'pointer', padding: '8px 12px', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            onClick={() => {
                              setShowResults(false);
                              setShowCreatePatient(true);
                            }}
                          >
                            <span>➕ CREAR PACIENTE NUEVO CON NOMBRE "{patientSearch}"</span>
                          </div>
                        </div>
                      )}
                    </div>
                  <button 
                    type="button" 
                    className="btn-add-patient"
                    onClick={() => setShowCreatePatient(true)}
                    title="Registrar nuevo paciente"
                  >
                    <UserPlus size={18} />
                  </button>
                </div>
              </div>
            </div>

              {initialData?.requester_name && (
                <div className="form-row" style={{ width: '100%' }}>
                  <div className="form-group full" style={{ width: '100%', flex: '1 1 100%', background: 'rgba(0, 229, 255, 0.05)', padding: '10px 15px', borderRadius: '8px', borderLeft: '3px solid #00e5ff', display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User size={16} color="#00e5ff" />
                      <div>
                        <span style={{ display: 'block', fontSize: '0.7rem', color: '#00e5ff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Solicitado por (Tercero)</span>
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-bright)' }}>{initialData.requester_name}</span>
                      </div>
                    </div>
                    {initialData.requester_phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Phone size={16} color="#00e5ff" />
                        <div>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: '#00e5ff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teléfono</span>
                          <span style={{ fontSize: '0.9rem', color: 'var(--text-bright)' }}>{initialData.requester_phone}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            <div className="form-row">
              <div className="form-group">
                <label>FECHA</label>
                <div className="input-icon-wrapper">
                  <CalendarIcon size={16} />
                  <input 
                    type="date" 
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>HORA</label>
                <div className="input-icon-wrapper">
                  <Clock size={16} />
                  <input 
                    type="time" 
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>OBRA SOCIAL</label>
                <input 
                  type="text" 
                  value={formData.insurance}
                  onChange={(e) => setFormData({...formData, insurance: e.target.value})}
                  placeholder="Ej: OSDE, Swiss Medical..."
                  className="modal-input"
                />
              </div>
              <div className="form-group">
                <label>PLAN</label>
                <input 
                  type="text" 
                  value={formData.plan}
                  onChange={(e) => setFormData({...formData, plan: e.target.value})}
                  placeholder="Ej: 210, 310..."
                  className="modal-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>TELÉFONO (RECORDATORIOS)</label>
                <input 
                  type="tel" 
                  value={formData.patient_phone || ''}
                  onChange={(e) => setFormData({...formData, patient_phone: e.target.value})}
                  placeholder="Ej: +54 9 11..."
                  className="modal-input"
                />
              </div>
              <div className="form-group">
                <label>CORREO ELECTRÓNICO</label>
                <input 
                  type="email" 
                  value={formData.patient_email || ''}
                  onChange={(e) => setFormData({...formData, patient_email: e.target.value})}
                  placeholder="Ej: paciente@email.com"
                  className="modal-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>ESPECIALIDAD</label>
                <div className="select-wrapper">
                  <select value={formData.specialty} disabled>
                    <option>{formData.specialty}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>PROFESIONAL</label>
                <div className="select-wrapper">
                  <select 
                    value={formData.professional}
                    onChange={(e) => {
                      const profName = e.target.value;
                      const prof = professionalsList.find(p => p.full_name === profName);
                      setFormData({...formData, professional: profName, specialty: prof?.specialty || 'CONSULTA GENERAL'});
                    }}
                    disabled={activeStaff.type === 'Profesional' && !!staffName}
                  >
                    {(activeStaff.type === 'Profesional' && !!staffName) ? (
                      <option value={staffName}>{staffName}</option>
                    ) : (
                      <>
                        {professionalsList.filter(p => p.type === 'Profesional' && !p.is_hidden).map(p => (
                          <option key={p.id} value={p.full_name}>{p.full_name}</option>
                        ))}
                      </>
                    )}
                  </select>
                  <ChevronDown size={16} className="select-icon" />
                </div>
              </div>
            </div>

            

            <div style={{ width: "100%", display: "block", marginBottom: "1.5rem" }}>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "#aaa", letterSpacing: "0.05em", paddingLeft: "0.25rem" }}>ARCHIVOS ADJUNTOS</label>
                <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                  {attachedFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '5px 10px', borderRadius: '4px' }}>
                      <FileText size={14} />
                      <a href={f.url} target="_blank" rel="noreferrer" style={{ color: '#00e5ff', fontSize: '12px', flex: 1 }}>{f.name}</a>
                      <button type="button" onClick={() => setAttachedFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'transparent', border: 'none', color: '#ff4444', cursor: 'pointer' }}><X size={14}/></button>
                    </div>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'rgba(0, 229, 255, 0.1)', border: '1px dashed #00e5ff', color: '#00e5ff', padding: '10px', borderRadius: '4px', cursor: 'pointer' }}>
                    {isUploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                    {isUploading ? 'SUBIENDO...' : 'SUBIR NUEVO ARCHIVO'}
                    <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} disabled={isUploading} />
                  </label>
                </div>
              </div>
            </div>

            <div style={{ width: "100%", display: "block", marginBottom: "1.5rem" }}>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "#aaa", letterSpacing: "0.05em", paddingLeft: "0.25rem" }}>OBSERVACIONES</label>
                <textarea 
                  placeholder="Ingrese notas internas del turno..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  style={{ width: "100%", minWidth: "100%" }}
                ></textarea>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'historial' && (
            <div style={{ padding: '1rem', width: '100%', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.2rem' }}>
                <h4 style={{ margin: 0, color: 'var(--text-bright)' }}>
                  Historial de Turnos de: <span style={{ color: 'var(--primary-cyan)' }}>{formData.patient || 'Paciente'}</span>
                </h4>
                
                <div style={{ display: 'flex', gap: '4px', background: 'rgba(0,0,0,0.3)', padding: '3px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                  <button 
                    type="button"
                    style={{
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      background: turnosSubTab === 'pasados' ? 'var(--primary-cyan)' : 'transparent',
                      color: turnosSubTab === 'pasados' ? '#000' : 'var(--text-main)'
                    }}
                    onClick={() => setTurnosSubTab('pasados')}
                  >
                    TURNOS PASADOS
                  </button>
                  <button 
                    type="button"
                    style={{
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      background: turnosSubTab === 'futuros' ? 'var(--primary-cyan)' : 'transparent',
                      color: turnosSubTab === 'futuros' ? '#000' : 'var(--text-main)'
                    }}
                    onClick={() => setTurnosSubTab('futuros')}
                  >
                    TURNOS FUTUROS
                  </button>
                  <button 
                    type="button"
                    style={{
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      background: turnosSubTab === 'todos' ? 'var(--primary-cyan)' : 'transparent',
                      color: turnosSubTab === 'todos' ? '#000' : 'var(--text-main)'
                    }}
                    onClick={() => setTurnosSubTab('todos')}
                  >
                    TODOS
                  </button>
                </div>
              </div>

              {(() => {
                const todayISODate = new Date().toISOString().split('T')[0];
                const pastApps = patientAppointments.filter(a => (a.appointment_date < todayISODate) || a.status === 'atendido' || a.status === 'completado');
                const futureApps = patientAppointments.filter(a => (a.appointment_date >= todayISODate) && a.status !== 'atendido' && a.status !== 'completado');

                const displayedList = turnosSubTab === 'pasados' ? pastApps : (turnosSubTab === 'futuros' ? futureApps : patientAppointments);

                if (displayedList.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '2rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', color: 'var(--text-muted)' }}>
                      No hay turnos registrados en esta categoría para este paciente.
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {displayedList.map(app => {
                      const isExpanded = expandedHistoryAppId === app.id;
                      const formattedDate = (() => {
                        if (!app.appointment_date) return 'Sin fecha';
                        const d = new Date(app.appointment_date + 'T00:00:00');
                        return isNaN(d) ? app.appointment_date : d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                      })();

                      const statusColor = app.status === 'atendido' ? '#00e5ff' : app.status === 'confirmado' ? '#00ff7f' : app.status === 'cancelado' ? '#ff5252' : '#ffca28';

                      let detailsObj = app.study_details || {};
                      if (typeof detailsObj === 'string') {
                        try { detailsObj = JSON.parse(detailsObj); } catch (e) { detailsObj = {}; }
                      }
                      const attached = detailsObj.attached_files || app.files || [];

                      return (
                        <div 
                          key={app.id}
                          style={{ 
                            background: 'var(--bg-lighter)', 
                            border: '1px solid var(--border-glass)', 
                            borderRadius: '10px', 
                            overflow: 'hidden' 
                          }}
                        >
                          <div 
                            onClick={() => setExpandedHistoryAppId(isExpanded ? null : app.id)}
                            style={{ 
                              padding: '0.8rem 1.2rem', 
                              display: 'flex', 
                              justify: 'space-between', 
                              alignItems: 'center', 
                              cursor: 'pointer',
                              background: isExpanded ? 'rgba(0, 229, 255, 0.06)' : 'transparent',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-bright)', textTransform: 'capitalize' }}>
                                {formattedDate}
                              </span>
                              <span style={{ fontSize: '0.85rem', color: 'var(--primary-cyan)', background: 'rgba(0, 229, 255, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>
                                ⏰ {app.appointment_time?.substring(0, 5) || '00:00'} hs
                              </span>
                              <span style={{ fontSize: '0.85rem', color: '#ffca28', fontWeight: 600 }}>
                                🏥 {app.professional_name || 'CONSULTORIOS EXTERNOS'}
                              </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: '12px', background: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}44` }}>
                                {app.status?.toUpperCase() || 'PENDIENTE'}
                              </span>
                              {isExpanded ? <ChevronUp size={18} color="var(--primary-cyan)" /> : <ChevronDown size={18} color="var(--text-muted)" />}
                            </div>
                          </div>

                          {isExpanded && (
                            <div style={{ padding: '1rem 1.2rem', borderTop: '1px solid var(--border-glass)', background: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                                <div>
                                  <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>ESTUDIO / ATENCIÓN</strong>
                                  <span style={{ color: 'var(--text-bright)', fontWeight: 600 }}>{app.study_type || 'Consulta General'}</span>
                                </div>
                                <div>
                                  <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>COBERTURA / PLAN</strong>
                                  <span style={{ color: '#ffd54f' }}>
                                    {detailsObj.insurance_provider || formData.insurance || 'PARTICULAR'} {detailsObj.insurance_plan ? `(${detailsObj.insurance_plan})` : ''}
                                  </span>
                                </div>
                                <div>
                                  <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>MONTO / PRECIO</strong>
                                  <span style={{ color: '#00ff7f', fontWeight: 600 }}>$ {app.price ? Number(app.price).toLocaleString('es-AR') : '0,00'}</span>
                                </div>
                                <div>
                                  <strong style={{ color: 'var(--text-muted)', display: 'block', fontSize: '0.75rem' }}>OTORGADO / ATENDIDO POR</strong>
                                  <span style={{ color: 'var(--primary-cyan)' }}>{app.created_by_user || 'Sistema'}</span>
                                </div>
                              </div>

                              <div style={{ marginTop: '10px', background: 'rgba(255,255,255,0.03)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <strong style={{ color: '#ffca28', display: 'block', fontSize: '0.75rem', marginBottom: '4px' }}>
                                  📝 OBSERVACIONES Y DETALLES DE LO REALIZADO EN ESTA ATENCIÓN:
                                </strong>
                                <p style={{ margin: 0, color: 'var(--text-main)', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                  {app.admin_notes || app.notes || detailsObj.reason || app.patient_message || '- Sin observaciones registradas en este turno -'}
                                </p>
                              </div>

                              {attached.length > 0 && (
                                <div style={{ marginTop: '10px' }}>
                                  <strong style={{ color: 'var(--primary-cyan)', display: 'block', fontSize: '0.75rem', marginBottom: '4px' }}>
                                    📎 ORDENES Y ARCHIVOS ADJUNTOS DE ESTA ATENCIÓN ({attached.length}):
                                  </strong>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {attached.map((f, i) => (
                                      <a key={i} href={f.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', textDecoration: 'none', border: '1px solid rgba(0,229,255,0.2)' }}>
                                        <Paperclip size={12} /> {f.name || `Archivo ${i+1}`}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="appointment-modal-footer">
          <button className="btn-cancel-modal" onClick={onClose}>
            CANCELAR
          </button>
          {!formData.id && (
            <button 
              className="btn-save-appointment" 
              style={{ 
                background: !!conflictAppointment ? 'rgba(255, 171, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)', 
                border: !!conflictAppointment ? '1px solid rgba(255, 171, 0, 0.5)' : '1px solid var(--primary-cyan)', 
                color: !!conflictAppointment ? '#f59e0b' : 'var(--primary-cyan)', 
                marginRight: '10px',
                cursor: 'pointer'
              }} 
              disabled={isSubmitting}
              title={!!conflictAppointment ? 'Atención: Existe una cita previa registrada en este horario.' : ''}
              onClick={async () => {
                if (isSubmitting) return;

                const finalPatient = (formData.patient || patientSearch || '').trim();
                if (!finalPatient) {
                  addNotification('Por favor ingrese o seleccione un paciente.', 'warning');
                  return;
                }
                if (!formData.date || !formData.time) {
                  addNotification('Por favor complete fecha y hora del turno.', 'warning');
                  return;
                }
                const rawP = formData.professional;
                const validProf = (rawP && !rawP.startsWith('VER '))
                  ? rawP 
                  : professionalsList?.filter(p => !p.is_hidden && p.type === 'Profesional')?.[0]?.full_name || '';

                if (!validProf) {
                  addNotification('Por favor seleccione un profesional válido.', 'warning');
                  return;
                }

                setIsSubmitting(true);
                try {
                  await onSave({
                    ...formData,
                    patient: finalPatient,
                    professional: validProf,
                    attached_files: attachedFiles
                  }, { keepOpen: true });

                  setFormData(prev => ({
                    patient_id: prev.patient_id,
                    patient: finalPatient,
                    patient_dni: prev.patient_dni,
                    patient_email: prev.patient_email,
                    patient_phone: prev.patient_phone,
                    insurance: prev.insurance,
                    plan: prev.plan,
                    professional: validProf,
                    studyType: 'CONSULTA GENERAL',
                    date: '',
                    time: '',
                    notes: ''
                  }));
                  setAttachedFiles([]);
                } catch (e) {
                  console.error('Error on save another appointment:', e);
                  addNotification('Error al guardar turno adicional: ' + (e.message || e), 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <Plus size={16} style={{ display: 'inline', marginRight: '5px' }} />
              AGREGAR OTRO
            </button>
          )}
          <button 
            className="btn-save-appointment" 
            disabled={isSubmitting}
            title={!!conflictAppointment ? 'Atención: Existe una cita previa registrada en este horario.' : ''}
            style={{
              cursor: 'pointer'
            }}
            onClick={async () => {
              if (isSubmitting) return;

              const finalPatient = (formData.patient || patientSearch || '').trim();
              if (!finalPatient) {
                addNotification('Por favor ingrese o seleccione un paciente.', 'warning');
                return;
              }
              if (!formData.date || !formData.time) {
                addNotification('Por favor complete fecha y hora del turno.', 'warning');
                return;
              }
              const rawP = formData.professional;
              const validProf = (rawP && !rawP.startsWith('VER '))
                ? rawP 
                : professionalsList?.filter(p => !p.is_hidden && p.type === 'Profesional')?.[0]?.full_name || '';

              if (!validProf) {
                addNotification('Por favor seleccione un profesional válido.', 'warning');
                return;
              }

              setIsSubmitting(true);
              try {
                await onSave({
                  ...formData,
                  patient: finalPatient,
                  professional: validProf,
                  attached_files: attachedFiles
                });
              } catch (e) {
                console.error("Error saving appointment in modal:", e);
                addNotification('Error al guardar el turno: ' + (e.message || e), 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'GUARDANDO...' : (formData.id ? 'GUARDAR CAMBIOS' : 'GUARDAR Y FINALIZAR')}
          </button>
        </div>

        {showCreatePatient && (
          <div className="new-patient-overlay">
            <div className="new-patient-header">
              <div className="header-title">
                <UserPlus size={20} />
                <span>NUEVO PACIENTE</span>
              </div>
              <button 
                type="button" 
                className="btn-close-modal" 
                onClick={() => setShowCreatePatient(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSaveNewPatient} className="new-patient-form">
              <div className="new-patient-body">
                <div className="form-row">
                  <div className="form-group full" style={{ position: "relative" }}>
                    <label>Nombre Completo *</label>
                    <input 
                      type="text" 
                      required 
                      placeholder="Ej: JUAN PEREZ"
                      value={newPatient.full_name}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setNewPatient({...newPatient, full_name: val});
                        searchNewPatients(val);
                      }}
                      onFocus={(e) => { if(e.target.value.length >= 2) setShowNewPatientResults(true); }}
                      autoFocus
                    />
                    
                    {showNewPatientResults && (
                      <div className="search-results-dropdown-modal" style={{ top: '100%', zIndex: 100 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: 'rgba(0,0,0,0.6)', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          <span>COINCIDENCIAS SIMILARES ({newPatientSearchResults.length})</span>
                          <button 
                            type="button" 
                            onClick={() => setShowNewPatientResults(false)}
                            style={{ background: 'none', border: 'none', color: '#ff5252', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                          >
                            ✕ CERRAR SUGERENCIAS
                          </button>
                        </div>

                        {newPatientSearchResults.length > 0 ? (
                          newPatientSearchResults.map(p => (
                            <div 
                              key={p.id} 
                              className="search-result-item"
                              onClick={() => {
                                selectPatient(p);
                                setShowCreatePatient(false);
                                addNotification('Paciente existente seleccionado', 'success');
                              }}
                            >
                              <div className="result-avatar"><User size={14} /></div>
                              <div className="result-info">
                                <span className="result-name">{p.full_name}</span>
                                <span className="result-dni" style={{ fontSize: '0.75rem', color: '#00e5ff' }}>
                                  DNI: {p.dni || 'Sin DNI'} {p.birthdate ? `| Nac: ${p.birthdate}` : ''}
                                </span>
                              </div>
                              <div className="result-insurance" style={{ fontSize: '0.75rem', color: '#ffd54f', fontWeight: 600 }}>
                                {p.insurance_provider || 'PARTICULAR'} {p.insurance_plan ? `(${p.insurance_plan})` : ''}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-results-msg" style={{ padding: '10px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#888' }}>No se encontraron coincidencias. Puede continuar creando.</span>
                          </div>
                        )}

                        <div 
                          className="search-result-item create-new-option"
                          style={{ background: 'rgba(0, 229, 255, 0.12)', borderTop: '1px solid rgba(0, 229, 255, 0.3)', color: '#00e5ff', cursor: 'pointer', padding: '8px 12px', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                          onClick={() => {
                            setShowNewPatientResults(false);
                          }}
                        >
                          <span>➕ CONTINUAR CREANDO CON NOMBRE "{newPatient.full_name}"</span>
                          <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(Ignorar lista)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>DNI / Documento *</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ej: 30123456"
                      value={newPatient.dni}
                      onChange={(e) => setNewPatient({...newPatient, dni: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Teléfono *</label>
                    <input 
                      type="tel" 
                      required
                      placeholder="Ej: +5491134567890"
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient({...newPatient, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full">
                    <label>Email</label>
                    <input 
                      type="email" 
                      placeholder="ejemplo@correo.com"
                      value={newPatient.email}
                      onChange={(e) => setNewPatient({...newPatient, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Obra Social</label>
                    <input 
                      type="text" 
                      value={newPatient.insurance_provider}
                      onChange={(e) => setNewPatient({...newPatient, insurance_provider: e.target.value})}
                      placeholder="Ej: OSDE, Swiss Medical..."
                      className="modal-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Plan</label>
                    <input 
                      type="text" 
                      value={newPatient.insurance_plan}
                      onChange={(e) => setNewPatient({...newPatient, insurance_plan: e.target.value})}
                      placeholder="Ej: 210, 310..."
                      className="modal-input"
                    />
                  </div>

                  {newPatient.insurance_provider !== 'PARTICULAR' && (
                    <div className="form-group">
                      <label>Nro. Afiliado</label>
                      <input 
                        type="text" 
                        placeholder="Ej: 12345678-00"
                        value={newPatient.affiliate_number}
                        onChange={(e) => setNewPatient({...newPatient, affiliate_number: e.target.value})}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="new-patient-footer">
                <button 
                  type="button" 
                  className="btn-cancel-modal" 
                  onClick={() => setShowCreatePatient(false)}
                >
                  CANCELAR
                </button>
                <button 
                  type="submit" 
                  className="btn-save-patient-action"
                  disabled={isSavingPatient}
                >
                  {isSavingPatient ? (
                    <>
                      <Loader2 size={16} className="spinning" style={{ marginRight: '0.5rem' }} />
                      GUARDANDO...
                    </>
                  ) : (
                    'GUARDAR Y SELECCIONAR'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AppointmentModal;
